import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Shield, Layers, Activity, Search, Filter, ArrowRight,
    CheckCircle2, Clock, Bot, Sparkles, ExternalLink,
    Plus, ChevronRight, Check, AlertTriangle, ShieldCheck,
    Cpu, Globe, Terminal, Play, Zap, RefreshCw, X, Radio,
    FileCode, Sliders, ArrowUpRight, BarChart2, LayoutGrid,
    List, User, ShieldAlert, ArrowLeftRight
} from "lucide-react";

export default function CasesBoard() {
    const navigate = useNavigate();
    const [casesData, setCasesData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("priority");
    const [viewMode, setViewMode] = useState("kanban"); // kanban | table
    const [includeArchived, setIncludeArchived] = useState(false);
    const [toast, setToast] = useState(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newCaseForm, setNewCaseForm] = useState({
        title: "",
        severity: "HIGH",
        categories: "ENDPOINT, MALWARE",
        description: "",
        assignee: "QueryTel Autonomous Responder"
    });

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchCases();
    }, []);

    const fetchCases = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/cases`, { headers });
            setCasesData(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load cases:", err);
        }
        setLoading(false);
    };

    const normalizeStatus = (status) => {
        if (!status) return "open";
        const s = status.toString().trim().toLowerCase().replace(/[\s-]+/g, "_");
        if (s.includes("prog") || s.includes("investigat") || s.includes("in_progress")) return "in_progress";
        if (s.includes("clos") || s.includes("resolv") || s.includes("contain") || s.includes("closed")) return "closed";
        return "open";
    };

    const handleMoveStatus = async (caseId, newStatus) => {
        const normStatus = normalizeStatus(newStatus);
        
        // 1. Optimistically update local React state immediately
        setCasesData(prev => {
            if (!prev || !prev.cases) return prev;
            const updatedCases = prev.cases.map(c => 
                (c.id === caseId || c._id === caseId || c.incidentId === caseId) ? { ...c, status: normStatus } : c
            );
            return {
                ...prev,
                counts: {
                    open: updatedCases.filter(c => normalizeStatus(c.status) === "open").length,
                    inProgress: updatedCases.filter(c => normalizeStatus(c.status) === "in_progress").length,
                    closed: updatedCases.filter(c => normalizeStatus(c.status) === "closed").length
                },
                cases: updatedCases
            };
        });

        const statusLabel = normStatus === 'in_progress' ? 'IN PROGRESS' : normStatus.toUpperCase();
        setToast(`Case ${caseId} moved to ${statusLabel}`);
        setTimeout(() => setToast(null), 2500);

        // 2. Persist to MongoDB backend
        try {
            const currentToken = localStorage.getItem("token");
            const authHeaders = currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
            await axios.patch(`${backendURL}/api/cases/${caseId}/status`, { status: normStatus }, { headers: authHeaders });
        } catch (err) {
            console.error("Failed to persist case status move:", err);
        }
    };

    const handleCreateCase = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                title: newCaseForm.title,
                severity: newCaseForm.severity,
                categories: newCaseForm.categories.split(",").map(c => c.trim().toUpperCase()),
                description: newCaseForm.description,
                assignee: newCaseForm.assignee
            };
            const currentToken = localStorage.getItem("token");
            const authHeaders = currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
            const res = await axios.post(`${backendURL}/api/cases`, payload, { headers: authHeaders });
            if (res.data.success) {
                setCasesData(prev => ({
                    ...prev,
                    cases: [res.data.case, ...(prev?.cases || [])],
                    total: (prev?.total || 0) + 1
                }));
                setCreateModalOpen(false);
                setNewCaseForm({
                    title: "",
                    severity: "HIGH",
                    categories: "ENDPOINT, MALWARE",
                    description: "",
                    assignee: "QueryTel Autonomous Responder"
                });
                setToast("Incident Case Created!");
                setTimeout(() => setToast(null), 3000);
            }
        } catch (err) {
            console.error("Failed to create case:", err);
        }
    };

    const cases = casesData?.cases || [];

    // Filter & Sort
    const filteredCases = cases.filter(c => {
        const matchesSearch =
            c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.categories?.some(cat => cat.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesSearch;
    });

    const severityWeight = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };

    const sortedCases = [...filteredCases].sort((a, b) => {
        if (sortBy === "priority") {
            return (severityWeight[b.severity] || 0) - (severityWeight[a.severity] || 0);
        }
        return new Date(b.timestamp) - new Date(a.timestamp);
    });

    const openCases = sortedCases.filter(c => normalizeStatus(c.status) === "open");
    const inProgressCases = sortedCases.filter(c => normalizeStatus(c.status) === "in_progress");
    const closedCases = sortedCases.filter(c => normalizeStatus(c.status) === "closed");

    const getSeverityBadge = (sev) => {
        switch (sev) {
            case "CRITICAL":
                return "bg-rose-500/15 text-rose-400 border-rose-500/30";
            case "HIGH":
                return "bg-amber-500/15 text-amber-400 border-amber-500/30";
            case "MEDIUM":
                return "bg-blue-500/15 text-blue-400 border-blue-500/30";
            default:
                return "bg-slate-700/30 text-slate-400 border-white/[0.08]";
        }
    };

    const renderCaseCard = (item) => (
        <div
            key={item.id || item._id}
            className="p-4 bg-[#111827] border border-white/[0.06] hover:border-white/[0.15] rounded-2xl space-y-3 shadow-md transition-all group"
        >
            {/* Card Header: Severity + ID + Assignee */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className={`text-[9.5px] font-mono uppercase px-2 py-0.5 rounded font-bold border ${getSeverityBadge(item.severity)}`}>
                        {item.severity}
                    </span>
                    <span className="text-[11px] font-mono text-slate-400 font-bold">{item.id}</span>
                </div>

                <div className="flex items-center gap-1.5" title={`Assignee: ${item.assignee}`}>
                    {item.isAi ? (
                        <div className="w-5 h-5 rounded-md bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-sm">
                            <Sparkles className="w-3 h-3" />
                        </div>
                    ) : (
                        <div className="w-5 h-5 rounded-md bg-blue-600 flex items-center justify-center text-[9px] font-bold text-white">
                            {item.assignee ? item.assignee.split(" ").filter(Boolean).map(s => s[0]).slice(0, 2).join("").toUpperCase() : "SOC"}
                        </div>
                    )}
                </div>
            </div>

            {/* Title */}
            <h4 className="text-[13px] font-bold text-white leading-snug group-hover:text-blue-400 transition-colors">
                {item.title}
            </h4>

            {/* Category Pills */}
            <div className="flex flex-wrap gap-1">
                {item.categories?.map((cat, cIdx) => (
                    <span key={cIdx} className="text-[8.5px] font-mono uppercase bg-blue-500/10 text-blue-300 px-1.5 py-0.5 rounded font-semibold border border-blue-500/20">
                        {cat}
                    </span>
                ))}
            </div>

            {/* Description Snippet */}
            <p className="text-[11.5px] text-slate-400 line-clamp-2 leading-relaxed">
                {item.description}
            </p>

            {/* Bottom Footer: Pending action + View case */}
            <div className="pt-2 border-t border-white/[0.04] flex items-center justify-between gap-2 text-[11px]">
                <span className="text-[10px] text-slate-400 font-mono truncate max-w-[150px]">
                    {item.pendingAction || "1 Pending action"}
                </span>

                <div className="flex items-center gap-1.5">
                    {/* Status Move Dropdown */}
                    <select
                        value={normalizeStatus(item.status)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                            e.stopPropagation();
                            handleMoveStatus(item.id, e.target.value);
                        }}
                        className="bg-[#1E293B] hover:bg-[#334155] text-slate-200 border border-white/[0.15] text-[10px] font-bold rounded-lg px-2 py-1 outline-none cursor-pointer transition-colors"
                    >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="closed">Closed</option>
                    </select>

                    <button
                        onClick={() => navigate(`/respond/cases/${item.id || 'CASE-4192'}`)}
                        className="text-blue-400 hover:text-blue-300 font-bold flex items-center gap-0.5 text-[11px] whitespace-nowrap"
                    >
                        View case →
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}

            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>01</span>
                <span>/</span>
                <span className="text-slate-400">Cases</span>
            </div>

            {/* ─── Page Title Banner ─── */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                    <h1 className="text-[26px] lg:text-[30px] font-black text-white tracking-tight">
                        Every case, from open to closed.
                    </h1>
                    <p className="text-[13px] text-slate-400 mt-1 max-w-3xl leading-relaxed">
                        Every alert QueryTel takes to a verdict becomes a case you can see, prioritize, and track. <strong>Open, in progress, and closed</strong>, with the full context, the verdict, and who is on it, in one board.
                    </p>
                </div>

                <div className="flex items-center gap-2.5 self-start lg:self-auto">
                    <button
                        onClick={() => setCreateModalOpen(true)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-[12px] font-bold shadow-lg shadow-blue-600/20 transition-all whitespace-nowrap"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Create Case
                    </button>
                </div>
            </div>

            {/* ─── Controls & Filter Bar ─── */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-[#111827] border border-white/[0.06] rounded-xl p-3 shadow-sm">
                
                <div className="flex items-center gap-2 flex-1">
                    {/* Search Input */}
                    <div className="flex items-center bg-black/40 border border-white/[0.08] rounded-lg px-3 py-1.5 flex-1 focus-within:border-blue-500/40 transition-colors">
                        <Search className="w-3.5 h-3.5 text-slate-500 mr-2" />
                        <input
                            type="text"
                            placeholder="Search cases by ID, title, entity, or category..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-transparent border-none outline-none text-[12px] text-white placeholder:text-slate-600 w-full"
                        />
                    </div>

                    {/* Sort Dropdown */}
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="bg-[#1E293B] text-slate-200 border border-white/[0.12] text-[11px] font-medium rounded-lg px-2.5 py-1.5 outline-none cursor-pointer"
                    >
                        <option value="priority" className="bg-[#0F172A]">Sort by: Priority</option>
                        <option value="newest" className="bg-[#0F172A]">Sort by: Newest</option>
                        <option value="oldest" className="bg-[#0F172A]">Sort by: Oldest</option>
                    </select>
                </div>

                <div className="flex items-center gap-3 self-start md:self-auto">
                    {/* Include Archived Toggle */}
                    <label className="flex items-center gap-2 text-[11.5px] text-slate-400 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={includeArchived}
                            onChange={(e) => setIncludeArchived(e.target.checked)}
                            className="rounded bg-black/40 border-white/[0.1] text-blue-600"
                        />
                        Include archived
                    </label>

                    {/* View Switcher */}
                    <div className="flex items-center bg-black/40 border border-white/[0.08] rounded-lg p-0.5 text-slate-400">
                        <button
                            onClick={() => setViewMode("kanban")}
                            className={`p-1.5 rounded transition-all ${
                                viewMode === "kanban" ? "bg-blue-600 text-white" : "hover:text-white"
                            }`}
                            title="Kanban Board"
                        >
                            <LayoutGrid className="w-3.5 h-3.5" />
                        </button>
                        <button
                            onClick={() => setViewMode("table")}
                            className={`p-1.5 rounded transition-all ${
                                viewMode === "table" ? "bg-blue-600 text-white" : "hover:text-white"
                            }`}
                            title="Table View"
                        >
                            <List className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* ─── KANBAN BOARD VIEW ─── */}
            {viewMode === "kanban" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
                    
                    {/* ═══ COLUMN 1: OPEN ═══ */}
                    <div className="bg-[#0F172A]/80 border border-white/[0.06] rounded-2xl p-4 space-y-3 min-h-[580px]">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-2.5">
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                                <h3 className="text-[13px] font-bold text-white">Open</h3>
                            </div>
                            <span className="text-[11px] font-mono text-slate-400 bg-white/[0.04] px-2 py-0.5 rounded-full">
                                {openCases.length}
                            </span>
                        </div>

                        <div className="space-y-3 overflow-y-auto max-h-[70vh] custom-scrollbar pr-1">
                            {openCases.length === 0 ? (
                                <p className="text-[11.5px] text-slate-500 italic text-center py-10">No open cases.</p>
                            ) : (
                                openCases.map(renderCaseCard)
                            )}
                        </div>
                    </div>

                    {/* ═══ COLUMN 2: IN PROGRESS ═══ */}
                    <div className="bg-[#0F172A]/80 border border-white/[0.06] rounded-2xl p-4 space-y-3 min-h-[580px]">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-2.5">
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-blue-400" />
                                <h3 className="text-[13px] font-bold text-white">In Progress</h3>
                            </div>
                            <span className="text-[11px] font-mono text-slate-400 bg-white/[0.04] px-2 py-0.5 rounded-full">
                                {inProgressCases.length}
                            </span>
                        </div>

                        <div className="space-y-3 overflow-y-auto max-h-[70vh] custom-scrollbar pr-1">
                            {inProgressCases.length === 0 ? (
                                <p className="text-[11.5px] text-slate-500 italic text-center py-10">No in-progress cases.</p>
                            ) : (
                                inProgressCases.map(renderCaseCard)
                            )}
                        </div>
                    </div>

                    {/* ═══ COLUMN 3: CLOSED ═══ */}
                    <div className="bg-[#0F172A]/80 border border-white/[0.06] rounded-2xl p-4 space-y-3 min-h-[580px]">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-2.5">
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                                <h3 className="text-[13px] font-bold text-white">Closed</h3>
                            </div>
                            <span className="text-[11px] font-mono text-slate-400 bg-white/[0.04] px-2 py-0.5 rounded-full">
                                {casesData?.counts?.closed || 0}
                            </span>
                        </div>

                        <div className="space-y-3 overflow-y-auto max-h-[70vh] custom-scrollbar pr-1">
                            {closedCases.length === 0 ? (
                                <p className="text-[11.5px] text-slate-500 italic text-center py-10">No closed cases.</p>
                            ) : (
                                closedCases.map(renderCaseCard)
                            )}
                        </div>
                    </div>

                </div>
            )}

            {/* ─── TABLE VIEW ─── */}
            {viewMode === "table" && (
                <div className="bg-[#111827] border border-white/[0.06] rounded-2xl overflow-hidden shadow-xl">
                    <table className="w-full text-left border-collapse text-[12px]">
                        <thead className="border-b border-white/[0.06] bg-black/40 text-[10.5px] uppercase font-mono text-slate-400">
                            <tr>
                                <th className="p-3">Case ID</th>
                                <th className="p-3">Severity</th>
                                <th className="p-3">Title</th>
                                <th className="p-3">Status</th>
                                <th className="p-3">Assignee</th>
                                <th className="p-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.04]">
                            {sortedCases.map(c => (
                                <tr key={c.id} className="hover:bg-white/[0.02] transition-colors">
                                    <td className="p-3 font-mono font-bold text-white">{c.id}</td>
                                    <td className="p-3">
                                        <span className={`text-[9.5px] font-mono uppercase px-2 py-0.5 rounded font-bold border ${getSeverityBadge(c.severity)}`}>
                                            {c.severity}
                                        </span>
                                    </td>
                                    <td className="p-3 font-medium text-slate-200">{c.title}</td>
                                    <td className="p-3">
                                        <span className="text-[10px] font-mono uppercase text-slate-400 bg-white/[0.04] px-2 py-0.5 rounded">
                                            {c.status.replace("_", " ")}
                                        </span>
                                    </td>
                                    <td className="p-3 text-slate-400">{c.assignee}</td>
                                    <td className="p-3 text-right">
                                        <button
                                            onClick={() => navigate(`/investigations/${c.linkedInvestigation || 'QueryTel-Enterprise-Infrastructure'}`)}
                                            className="text-blue-400 hover:text-blue-300 font-bold"
                                        >
                                            View →
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ─── Create Case Modal ─── */}
            {createModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <h3 className="text-[15px] font-bold text-white">Create Incident Case</h3>
                            <button onClick={() => setCreateModalOpen(false)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateCase} className="space-y-3.5 text-[12px]">
                            <div>
                                <label className="text-slate-400 block mb-1">Case Title</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Cobalt Strike lateral movement detected"
                                    value={newCaseForm.title}
                                    onChange={(e) => setNewCaseForm({ ...newCaseForm, title: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none focus:border-blue-500"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-slate-400 block mb-1">Severity</label>
                                    <select
                                        value={newCaseForm.severity}
                                        onChange={(e) => setNewCaseForm({ ...newCaseForm, severity: e.target.value })}
                                        className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                    >
                                        <option value="CRITICAL">CRITICAL</option>
                                        <option value="HIGH">HIGH</option>
                                        <option value="MEDIUM">MEDIUM</option>
                                        <option value="LOW">LOW</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-slate-400 block mb-1">Assignee</label>
                                    <select
                                        value={newCaseForm.assignee}
                                        onChange={(e) => setNewCaseForm({ ...newCaseForm, assignee: e.target.value })}
                                        className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                    >
                                        {localStorage.getItem("userName") && (
                                            <option value={localStorage.getItem("userName")}>{localStorage.getItem("userName")} (You)</option>
                                        )}
                                        <option value="Musaab Syed">Musaab Syed</option>
                                        <option value="Owais Hassan">Owais Hassan</option>
                                        <option value="QueryTel Autonomous Responder">QueryTel Autonomous Responder</option>
                                        <option value="QueryTel SOC Lead">QueryTel SOC Lead</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Categories (Comma separated)</label>
                                <input
                                    type="text"
                                    placeholder="ENDPOINT, MALWARE, RANSOMWARE"
                                    value={newCaseForm.categories}
                                    onChange={(e) => setNewCaseForm({ ...newCaseForm, categories: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none uppercase font-mono text-[11px]"
                                />
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Description</label>
                                <textarea
                                    rows="3"
                                    placeholder="Details of the incident, impact, and initial findings..."
                                    value={newCaseForm.description}
                                    onChange={(e) => setNewCaseForm({ ...newCaseForm, description: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none text-[11.5px]"
                                />
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                                <button
                                    type="button"
                                    onClick={() => setCreateModalOpen(false)}
                                    className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md"
                                >
                                    Create Case
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
